// AgoraRoom — Motor de video Agora RTC para clases en vivo JASQ®
// Soporta hasta 100 usuarios con miniaturas adaptables.
import { useEffect, useState, useRef, useCallback } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import { api, getAccessToken, formatApiErrorDetail } from "@/lib/api";
import { cameraProfile, profileKey, SCREEN_PROFILE, HD_BUDGET, SCREEN_PIXELS } from "@/lib/videoBudget";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Mic, MicOff, Video, VideoOff, PhoneOff, MonitorUp, Users,
  Loader2, AlertCircle, Settings, Volume2, MessageCircle, X, Send, Link2,
} from "lucide-react";

const APP_ID = process.env.REACT_APP_AGORA_APP_ID;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const WS_BASE = BACKEND_URL.replace(/^http/, "ws");

// v5.3.0 · Perfil de video por presupuesto HD (ver lib/videoBudget.js): nunca se supera HD sumado por persona.
function getVideoProfile(count) {
  return cameraProfile(count);
}

function mediaErrorMessage(e, what) {
  const n = `${e?.name || ""} ${e?.code || ""} ${e?.message || ""}`;
  if (/NotAllowed|PERMISSION_DENIED/i.test(n))
    return `El navegador tiene bloqueado ${what}. Pulsa el candado junto a la dirección, permite ${what} y vuelve a intentarlo.`;
  if (/NotReadable|NOT_READABLE/i.test(n))
    return `Otra aplicación o pestaña está usando ${what}. Ciérrala y vuelve a intentarlo.`;
  if (/NotFound|DEVICES_NOT_FOUND/i.test(n))
    return `No se encontró ${what} en este equipo.`;
  return `No se pudo activar ${what}. Intenta de nuevo.`;
}

// Mensaje claro cuando falla la conexión al servicio de video (en vez del error técnico).
function connectionErrorMessage(err) {
  const raw = `${err?.code || ""} ${err?.name || ""} ${err?.message || ""}`;
  if (/CAN_NOT_GET_GATEWAY_SERVER|GATEWAY|NETWORK_ERROR|WS_ABORT|DISCONNECT/i.test(raw))
    return "No se pudo conectar al servicio de video. Revisa tu internet (o desactiva VPN/bloqueadores). Si en otra red también falla, avisa al administrador: el servicio de video puede estar sin disponibilidad.";
  if (/INVALID_TOKEN|TOKEN|DYNAMIC_KEY|APP_ID|VENDOR_KEY/i.test(raw))
    return "El acceso a la sala de video no es válido o venció. Sal e intenta entrar de nuevo.";
  if (/UID_CONFLICT/i.test(raw))
    return "Ya estás conectado a esta clase desde otra pestaña o dispositivo.";
  return err?.message || "Error al conectar con la sala de video";
}

function RemoteUser({ user, isLarge, identity, onPin, className: extraClass }) {
  const ref = useRef(null);
  useEffect(() => {
    if (user.videoTrack && ref.current) {
      user.videoTrack.play(ref.current, { fit: "contain" });
    }
  }, [user.videoTrack]);
  useEffect(() => {
    if (user.audioTrack) user.audioTrack.play();
  }, [user.audioTrack]);

  const displayName = identity?.name || `UID ${user.uid}`;

  return (
    <div
      onDoubleClick={() => onPin?.(user.uid)}
      className={`relative bg-[#1A1D2E] rounded-xl overflow-hidden border border-white/10 cursor-pointer ${
        isLarge ? "col-span-2 row-span-2" : ""
      } ${extraClass || ""}`}
      data-testid={`remote-user-${user.uid}`}
    >
      <div ref={ref} className="absolute inset-0" />
      {!user.videoTrack && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-[#A43790]/20 flex items-center justify-center">
            <Users size={24} className="text-[#A43790]" />
          </div>
        </div>
      )}
      <div className="absolute bottom-1.5 left-1.5 bg-black/60 backdrop-blur-sm rounded-lg px-2 py-0.5 flex items-center gap-1.5">
        {user.audioTrack ? <Volume2 size={10} className="text-[#3AE880]" /> : <MicOff size={10} className="text-red-400" />}
        <span className="text-[10px] font-bold text-white/80">{displayName}</span>
      </div>
    </div>
  );
}

// v5.5.3 · campanita corta para avisar que alguien espera ser admitido (sin archivos de audio).
function playDing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const tone = (freq, t0, dur) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + t0);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t0 + dur);
      o.connect(g); g.connect(ctx.destination);
      o.start(ctx.currentTime + t0); o.stop(ctx.currentTime + t0 + dur + 0.05);
    };
    tone(880, 0, 0.25); tone(1320, 0.22, 0.4);
    setTimeout(() => { try { ctx.close(); } catch { /* ya cerrado */ } }, 1200);
  } catch { /* el navegador bloqueó el audio: no pasa nada */ }
}

export default function AgoraRoom({ channelName, classId, onLeave, isRecording }) {
  const { user: authUser } = useAuth();
  const [joined, setJoined] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allRemoteUsers, setRemoteUsers] = useState([]);
  // v5.7.3 · aviso al iniciar la videollamada: hay que permitir cámara y micrófono
  const [permState, setPermState] = useState("pending"); // pending | granted | partial | denied
  const [permBannerClosed, setPermBannerClosed] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  const [shareWithAudio, setShareWithAudio] = useState(false);
  const [identities, setIdentities] = useState({});
  // Solo mostramos participantes reales: los que publican audio/video o ya se
  // identificaron por el canal. Evita "fantasmas" (sesiones que quedaron colgadas
  // en Agora, el bot de grabación, etc.) que solo disparan user-joined.
  const remoteUsers = allRemoteUsers.filter((u) => u.videoTrack || u.audioTrack || identities[u.uid]);
  const [sharingUid, setSharingUid] = useState(null); // {agora_uid: {name}}
  const lastProfileKeyRef = useRef(null);
  const usageSessionRef = useRef(null);
  const hasVideoNowRef = useRef(false);
  const [pinnedUid, setPinnedUid] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [unreadChat, setUnreadChat] = useState(0);
  const chatOpenRef = useRef(false);
  chatOpenRef.current = chatOpen;
  useEffect(() => { if (chatOpen) setUnreadChat(0); }, [chatOpen]);
  const [chatInput, setChatInput] = useState("");
  const chatEndRef = useRef(null);
  const [guestLink, setGuestLink] = useState(null);
  const [guestLinkLoading, setGuestLinkLoading] = useState(false);
  const [guestRequests, setGuestRequests] = useState([]);
  const [guestPanelOpen, setGuestPanelOpen] = useState(false);
  const knownGuestIdsRef = useRef(new Set()); // solicitudes ya avisadas (sonido una sola vez por persona)
  const [guestBusy, setGuestBusy] = useState(false);

  const clientRef = useRef(null);
  const localVideoRef = useRef(null);
  const localVideoTrackRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const screenTrackRef = useRef(null);
  const screenAudioTrackRef = useRef(null);
  const shareAnnounceIntervalRef = useRef(null);
  const localContainerRef = useRef(null);
  const floatCamRef = useRef(null);
  // v5.7.7 · miniaturas propias flotantes: se arrastran con el dedo/ratón y se pueden minimizar (no tapan la pantalla compartida)
  const [floatPos, setFloatPos] = useState({ localThumb: null, shareCam: null });
  const [floatMin, setFloatMin] = useState({ localThumb: false, shareCam: false });
  const dragRef = useRef(null);
  const onDragDown = (key) => (e) => {
    if (e.target.closest("[data-nodrag]")) return;
    const box = e.currentTarget.getBoundingClientRect();
    const par = e.currentTarget.offsetParent?.getBoundingClientRect() || { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
    dragRef.current = { key, dx: e.clientX - box.left, dy: e.clientY - box.top, par, w: box.width, h: box.height };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
  };
  const onDragMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const x = Math.max(0, Math.min(d.par.width - d.w, e.clientX - d.par.left - d.dx));
    const y = Math.max(0, Math.min(d.par.height - d.h, e.clientY - d.par.top - d.dy));
    setFloatPos((p) => ({ ...p, [d.key]: { x, y } }));
  };
  const onDragEnd = () => { dragRef.current = null; };
  const floatStyle = (key) => (floatPos[key] ? { left: floatPos[key].x, top: floatPos[key].y, right: "auto", bottom: "auto", touchAction: "none" } : { touchAction: "none" });
  const joinedRef = useRef(false);       // Guard: prevent double-joins
  const channelRef = useRef(channelName); // Stable ref for async callbacks
  const controlWsRef = useRef(null);
  const identitiesRef = useRef({});
  channelRef.current = channelName;

  // v5.5.3 · también el franquiciado dueño de la clase y el super admin pueden admitir (el servidor valida los permisos reales)
  const isModerator = ["admin", "docente", "franquiciado"].includes(authUser?.role) || !!authUser?.is_super_admin;

  // Permiso de compartir pantalla para ESTUDIANTES (lo decide el docente).
  const [studentsCanShare, setStudentsCanShare] = useState(false);
  const canShareScreen = authUser?.role !== "estudiante" || studentsCanShare;
  useEffect(() => {
    if (!classId) return;
    api.get(`/live-classes/${classId}/screen-share-permission`)
      .then(({ data }) => setStudentsCanShare(!!data?.allowed))
      .catch(() => {});
  }, [classId]);
  // v5.6.3 · respaldo: si el aviso por WebSocket no llega (bloqueado o el docente es un
  // gestor sin ser dueño de la clase), el alumno consulta el permiso cada 5 s.
  useEffect(() => {
    if (!classId || authUser?.role !== "estudiante") return undefined;
    let alive = true;
    const iv = setInterval(() => {
      api.get(`/live-classes/${classId}/screen-share-permission`)
        .then(({ data }) => {
          if (!alive) return;
          const allowed = !!data?.allowed;
          setStudentsCanShare((prev) => {
            if (prev && !allowed && screenTrackRef.current) stopScreenShareRef.current?.();
            return allowed;
          });
        })
        .catch(() => {});
    }, 5000);
    return () => { alive = false; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, authUser?.role]);
  const toggleStudentsCanShare = async () => {
    const next = !studentsCanShare;
    setStudentsCanShare(next);
    try {
      await api.post(`/live-classes/${classId}/screen-share-permission`, { allowed: next });
      const ws = controlWsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "control", action: "screen_share_permission", data: { allowed: next } }));
      }
      toast.success(next ? "Los estudiantes pueden compartir pantalla" : "Los estudiantes ya no pueden compartir pantalla");
    } catch (e) {
      setStudentsCanShare(!next);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo cambiar el permiso.");
    }
  };

  const sendChatMessage = () => {
    const text = chatInput.trim();
    if (!text || !controlWsRef.current) return;
    controlWsRef.current.send(JSON.stringify({
      type: "presence",
      data: { kind: "chat_message", agora_uid: uid.current, name: authUser?.name || "Usuario", text },
    }));
    setChatMessages((prev) => {
      const next = [...prev, { name: "Tú", text, mine: true, ts: Date.now() }];
      return next.length > 50 ? next.slice(-50) : next;
    });
    setChatInput("");
  };

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMessages]);

  const uid = useRef(0); // UID se genera fresco en joinChannel

  // Join channel — uses refs for channel/uid so it's stable across renders.
  // Fix 2.1.2: Acepta `activeRef` para abortar si StrictMode desmonta antes
  // de completar el join async. Genera UID fresco cada intento.
  const joinChannel = useCallback(async (activeRef) => {
    if (joinedRef.current) return;
    const ch = channelRef.current;
    if (!ch) return;

    // UID fresco: evita UID_CONFLICT si StrictMode ejecuta mount→unmount→mount
    const freshUid = Math.floor(Math.random() * 900000) + 100000;
    uid.current = freshUid;

    try {
      setLoading(true);
      setError(null);

      // Pedir los permisos de cámara/micrófono ANTES de conectar: así el
      // navegador pregunta al usuario aunque la conexión al servicio falle después.
      try {
        const probe = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        probe.getTracks().forEach((t) => t.stop());
        setPermState("granted");
      } catch (_) {
        try {
          const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
          probe.getTracks().forEach((t) => t.stop());
          setPermState("partial");
        } catch (_) { setPermState("denied"); /* sin permiso: se avisará al activar micrófono/cámara */ }
      }

      const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
      clientRef.current = client;

      client.on("user-published", async (user, mediaType) => {
        let subscribed = false;
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            await client.subscribe(user, mediaType);
            subscribed = true;
            break;
          } catch (e) {
            if (attempt < 3) await new Promise(r => setTimeout(r, 800 * attempt));
          }
        }
        if (!subscribed) return;
        setRemoteUsers((prev) => {
          const exists = prev.find((u) => u.uid === user.uid);
          if (exists) return prev.map((u) => u.uid === user.uid ? { ...u, [mediaType + "Track"]: user[mediaType + "Track"] } : u);
          return [...prev, { uid: user.uid, videoTrack: user.videoTrack, audioTrack: user.audioTrack }];
        });
      });

      client.on("user-unpublished", (user, mediaType) => {
        setRemoteUsers((prev) =>
          prev.map((u) => u.uid === user.uid ? { ...u, [mediaType + "Track"]: null } : u)
        );
      });

      client.on("user-left", (user) => {
        setRemoteUsers((prev) => prev.filter((u) => u.uid !== user.uid));
      });

      // Alguien que se une pero nunca publica audio/video (ej. un invitado
      // que se queda callado) igual debe aparecer en la lista de presentes.
      client.on("user-joined", (user) => {
        setRemoteUsers((prev) =>
          prev.find((u) => u.uid === user.uid) ? prev : [...prev, { uid: user.uid, videoTrack: null, audioTrack: null }]
        );
      });

      client.on("token-privilege-will-expire", async () => {
        try {
          const { data } = await api.post("/agora/token", {
            channel: channelRef.current, uid: uid.current, role: "publisher", renew: true,
          });
          await client.renewToken(data.token);
        } catch (e) {
          console.error("Token renewal failed:", e);
        }
      });

      const { data: tokenData } = await api.post("/agora/token", {
        channel: ch, uid: freshUid, role: "publisher",
      });

      usageSessionRef.current = tokenData.usage_session || null; // v5.3.0 · medición de minutos reales
      await client.join(tokenData.appId, ch, tokenData.token, freshUid);

      // StrictMode guard: si el componente fue desmontado durante el join
      // async, abortar limpiamente sin publicar tracks.
      if (activeRef && !activeRef.current) {
        await client.leave().catch(() => {});
        if (clientRef.current === client) clientRef.current = null;
        return;
      }

      // WS de presencia: identificar agora_uid → nombre real
      const connectPresenceWs = () => {
        if (!activeRef.current) return;
        try {
          const token = getAccessToken();
          const wsUrl = `${WS_BASE}/api/ws/whiteboard/${ch}?token=${encodeURIComponent(token)}`;
          const ws = new WebSocket(wsUrl);
          controlWsRef.current = ws;
          const announce = () => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({
                type: "presence",
                data: { kind: "agora_identify", agora_uid: freshUid, name: authUser?.name || "Usuario" },
              }));
            }
          };
          ws.onopen = () => {
            announce();
            const iv = setInterval(announce, 8000);
            ws.addEventListener("close", () => clearInterval(iv));
          };
          ws.onmessage = async (evt) => {
            try {
              const msg = JSON.parse(evt.data);
              if (msg.type === "presence" && msg.data?.kind === "agora_identify") {
                const { agora_uid, name } = msg.data;
                if (agora_uid && name) {
                  setIdentities((prev) => {
                    const next = { ...prev, [agora_uid]: { name } };
                    identitiesRef.current = next;
                    return next;
                  });
                }
              }
              if (msg.type === "control" && msg.action === "screen_share_permission") {
                const allowed = !!msg.data?.allowed;
                setStudentsCanShare(allowed);
                if (!allowed && authUser?.role === "estudiante" && screenTrackRef.current) {
                  stopScreenShareRef.current?.();
                  toast.info("El docente desactivó compartir pantalla.");
                }
              }
              if (msg.type === "presence" && msg.data?.kind === "screen_share_started") {
                const senderUid = msg.data.agora_uid;
                setSharingUid(senderUid);
                if (senderUid && senderUid !== freshUid && screenTrackRef.current) {
                  const cl = clientRef.current;
                  if (cl) {
                    await cl.unpublish(screenTrackRef.current).catch(() => {});
                    screenTrackRef.current.close();
                    screenTrackRef.current = null;
                    if (localVideoTrackRef.current) {
                      await cl.publish(localVideoTrackRef.current).catch(() => {});
                      if (localContainerRef.current) localVideoTrackRef.current.play(localContainerRef.current, { fit: "contain" });
                    }
                    setScreenSharing(false);
                  }
                  const who = identitiesRef.current[senderUid]?.name || `UID ${senderUid}`;
                  toast.info(`${who} está compartiendo pantalla ahora.`);
                }
              }
              if (msg.type === "presence" && msg.data?.kind === "screen_share_stopped") {
                if (msg.data.agora_uid) setSharingUid((prev) => prev === msg.data.agora_uid ? null : prev);
              }
              if (msg.type === "chat_history" && Array.isArray(msg.data)) {
                const myId = authUser?.id;
                setChatMessages(msg.data.slice(-50).map((m) => ({
                  name: m.user_id === myId ? "Tú" : (m.name || "Usuario"),
                  text: m.text, mine: m.user_id === myId, ts: m.ts,
                })));
              }
              if (msg.type === "presence" && msg.data?.kind === "chat_message") {
                const { name, text, agora_uid } = msg.data;
                if (text) {
                  if (agora_uid !== freshUid && !chatOpenRef.current) setUnreadChat((n) => n + 1);
                  setChatMessages((prev) => {
                    const next = [...prev, { name: name || "Usuario", text, mine: agora_uid === freshUid, ts: Date.now() }];
                    return next.length > 50 ? next.slice(-50) : next;
                  });
                }
              }
              if (msg.type === "guest_request") {
                setGuestRequests((prev) => (prev.some((g) => g.id === msg.guest_id) ? prev : [...prev, { id: msg.guest_id, name: msg.name }]));
                if (isModerator && !knownGuestIdsRef.current.has(msg.guest_id)) {
                  knownGuestIdsRef.current.add(msg.guest_id);
                  playDing();
                }
              }
            } catch { /* ignore parse errors */ }
          };
          ws.onerror = () => {};
          ws.onclose = () => {
            if (activeRef.current) {
              setTimeout(connectPresenceWs, 2000);
            }
          };
        } catch { /* WS no crítico */ }
      };
      connectPresenceWs();

      // Crear tracks independientes: audio obligatorio, video opcional
      let audioTrack;
      let videoTrack = null;
      let cameraFailed = false;

      let micFailed = false;
      try {
        audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
          encoderConfig: "speech_standard", AEC: true, ANS: true, AGC: true,
        });
      } catch (micErr) {
        console.warn("No se pudo crear audio track:", micErr);
        audioTrack = null;
        micFailed = true;
      }

      try {
        videoTrack = await AgoraRTC.createCameraVideoTrack({ encoderConfig: getVideoProfile(1) });
      } catch (camErr) {
        console.warn("No se pudo crear video track:", camErr);
        cameraFailed = true;
      }

      // Guard post-tracks: si desmontado mientras se creaban tracks, cerrarlos
      if (activeRef && !activeRef.current) {
        audioTrack?.close();
        videoTrack?.close();
        await client.leave().catch(() => {});
        if (clientRef.current === client) clientRef.current = null;
        return;
      }

      localAudioTrackRef.current = audioTrack;
      localVideoTrackRef.current = videoTrack;

      if (videoTrack && localContainerRef.current) {
        videoTrack.play(localContainerRef.current, { fit: "contain" });
      }

      const tracksToPublish = [audioTrack, videoTrack].filter(Boolean);
      if (tracksToPublish.length) await client.publish(tracksToPublish);
      joinedRef.current = true;
      setJoined(true);

      if (micFailed) {
        setMicOn(false);
        toast.warning(mediaErrorMessage(null, "el micrófono") + " Puedes activarlo con el botón del micrófono.", { duration: 8000 });
      }
      try {
        if (!sessionStorage.getItem("jasq_headphones_tip")) {
          sessionStorage.setItem("jasq_headphones_tip", "1");
          toast.info("Tip: usa audífonos para evitar eco en la clase.", { duration: 6000 });
        }
      } catch (_) { /* sin storage */ }

      if (cameraFailed) {
        setCamOn(false);
        toast.warning("No se pudo acceder a tu cámara. Te uniste solo con audio.");
      }
    } catch (err) {
      // Ignorar errores si ya se abortó
      if (activeRef && !activeRef.current) return;
      console.error("Agora join error:", err);
      setError(connectionErrorMessage(err));
    } finally {
      if (!activeRef || activeRef.current) setLoading(false);
    }
  }, []);

  // Mount-only effect con activeRef para StrictMode.
  // mount → activeRef.current=true → joinChannel ejecuta completo
  // StrictMode cleanup → activeRef.current=false → joinChannel aborta
  // StrictMode re-mount → nuevo activeRef.current=true → joinChannel con UID fresco
  useEffect(() => {
    const activeRef = { current: true };

    if (APP_ID && channelName) joinChannel(activeRef);

    return () => {
      activeRef.current = false;
      joinedRef.current = false;
      leaveChannel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle actual page unload (tab close, browser close) — NOT SPA navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      localAudioTrackRef.current?.close();
      localVideoTrackRef.current?.close();
      screenTrackRef.current?.close();
      screenAudioTrackRef.current?.close();
      if (shareAnnounceIntervalRef.current) clearInterval(shareAnnounceIntervalRef.current);
      try { controlWsRef.current?.close(); } catch { /* */ }
      clientRef.current?.leave().catch(() => {});
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Auto-adjust video quality based on participant count
  useEffect(() => {
    // Videos que recibe cada otra persona ≈ cámaras remotas visibles; con pantalla compartida, ésta reserva parte del presupuesto.
    const remoteVideo = remoteUsers.filter((u) => u.videoTrack).length;
    const sharing = !!sharingUid;
    const profile = sharing
      ? cameraProfile(Math.max(1, remoteVideo - 1), HD_BUDGET - SCREEN_PIXELS)
      : cameraProfile(Math.max(1, remoteVideo));
    const key = profileKey(profile);
    if (localVideoTrackRef.current && lastProfileKeyRef.current !== key) {
      lastProfileKeyRef.current = key;
      localVideoTrackRef.current.setEncoderConfiguration(profile).catch?.(() => {});
    }
  }, [remoteUsers.filter((u) => u.videoTrack).length, sharingUid]); // eslint-disable-line react-hooks/exhaustive-deps

  // v5.3.0 · Latido de consumo cada 60 s (¿recibe video? → Agora lo cobra como video) y cierre al salir/cerrar la pestaña.
  hasVideoNowRef.current = remoteUsers.some((u) => u.videoTrack);
  const sendUsage = useCallback((left = false, beacon = false) => {
    const sid = usageSessionRef.current;
    if (!sid) return;
    const body = { session: sid, video: hasVideoNowRef.current, left };
    if (beacon && navigator.sendBeacon) {
      try {
        navigator.sendBeacon(`${api.defaults.baseURL || "/api"}/video/usage/ping`, new Blob([JSON.stringify(body)], { type: "application/json" }));
        return;
      } catch { /* cae al post normal */ }
    }
    api.post("/video/usage/ping", body).catch(() => {});
  }, []);
  useEffect(() => {
    if (!joined) return undefined;
    const iv = setInterval(() => sendUsage(false), 60000);
    const onHide = () => sendUsage(true, true);
    window.addEventListener("pagehide", onHide);
    return () => { clearInterval(iv); window.removeEventListener("pagehide", onHide); };
  }, [joined, sendUsage]);

  // Evita que una operación del SDK de Agora que nunca responde (posible bajo
  // carga con muchos participantes) deje al usuario con un botón colgado.
  const withTimeout = (promise, ms, label) => Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`timeout:${label}`)), ms)),
  ]);

  const leaveChannel = async () => {
    sendUsage(true);
    const client = clientRef.current;
    localAudioTrackRef.current?.close();
    localVideoTrackRef.current?.close();
    screenTrackRef.current?.close();
    screenAudioTrackRef.current?.close();
    if (shareAnnounceIntervalRef.current) clearInterval(shareAnnounceIntervalRef.current);
    try { controlWsRef.current?.close(); } catch { /* */ }
    controlWsRef.current = null;
    if (client) {
      try {
        await withTimeout(client.leave(), 6000, "leave");
      } catch (e) {
        console.warn("[AgoraRoom] client.leave() no respondió a tiempo, se limpia igual:", e);
      }
      if (clientRef.current === client) clientRef.current = null;
    }
    joinedRef.current = false;
    setJoined(false);
    setRemoteUsers([]);
  };

  const toggleMic = async () => {
    if (!localAudioTrackRef.current) {
      // No se pudo crear al entrar (permiso/dispositivo): reintentar ahora.
      try {
        const t = await AgoraRTC.createMicrophoneAudioTrack({ encoderConfig: "speech_standard", AEC: true, ANS: true, AGC: true });
        localAudioTrackRef.current = t;
        if (clientRef.current) await clientRef.current.publish(t);
        setMicOn(true);
      } catch (e) {
        console.warn("[AgoraRoom] reintento de micrófono falló:", e);
        toast.error(mediaErrorMessage(e, "el micrófono"), { duration: 8000 });
      }
      return;
    }
    try {
      await withTimeout(localAudioTrackRef.current.setEnabled(!micOn), 5000, "mic");
      setMicOn(!micOn);
    } catch (e) {
      console.warn("[AgoraRoom] toggleMic no respondió a tiempo:", e);
      toast.error("No se pudo cambiar el micrófono. Intenta de nuevo.");
    }
  };

  const toggleCam = async () => {
    if (!localVideoTrackRef.current) {
      // No se pudo crear al entrar (permiso/dispositivo): reintentar ahora.
      try {
        const t = await AgoraRTC.createCameraVideoTrack({ encoderConfig: getVideoProfile(1) });
        localVideoTrackRef.current = t;
        if (clientRef.current && !screenTrackRef.current) await clientRef.current.publish(t);
        setCamOn(true);
        if (localContainerRef.current) t.play(localContainerRef.current, { fit: "contain" });
      } catch (e) {
        console.warn("[AgoraRoom] reintento de cámara falló:", e);
        toast.error(mediaErrorMessage(e, "la cámara"), { duration: 8000 });
      }
      return;
    }
    try {
      await withTimeout(localVideoTrackRef.current.setEnabled(!camOn), 5000, "cam");
      setCamOn(!camOn);
    } catch (e) {
      console.warn("[AgoraRoom] toggleCam no respondió a tiempo:", e);
      toast.error("No se pudo cambiar la cámara. Intenta de nuevo.");
    }
  };

  const handleGuestLink = async () => {
    if (guestLink) {
      await navigator.clipboard.writeText(guestLink).catch(() => {});
      toast.success("Link copiado al portapapeles.");
      return;
    }
    setGuestLinkLoading(true);
    try {
      const { data } = await api.get(`/live-classes/${classId}/guest-link`);
      let url = data?.guest_url;
      if (!url) {
        const res = await api.post(`/live-classes/${classId}/guest-link`);
        url = res.data?.guest_url;
      }
      if (url) {
        setGuestLink(url);
        await navigator.clipboard.writeText(url).catch(() => {});
        toast.success("Link de invitado generado y copiado.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo generar el link.");
    } finally {
      setGuestLinkLoading(false);
    }
  };

  const dropGuest = (guestId) => setGuestRequests((prev) => prev.filter((g) => g.id !== guestId));
  const guestError = (e, fallback) => {
    const st = e?.response?.status;
    if (st === 404) return null; // ya procesada (por ejemplo desde otra ventana): solo se quita de la lista
    return formatApiErrorDetail(e?.response?.data?.detail) || fallback;
  };
  const approveGuest = async (guestId) => {
    try {
      await api.post(`/live-classes/${classId}/guest-requests/${guestId}/approve`);
      dropGuest(guestId);
      toast.success("Invitado admitido");
    } catch (e) {
      const msg = guestError(e, "No se pudo admitir.");
      if (msg) toast.error(msg); else dropGuest(guestId);
    }
  };
  const rejectGuest = async (guestId) => {
    try {
      await api.post(`/live-classes/${classId}/guest-requests/${guestId}/reject`);
      dropGuest(guestId);
    } catch (e) {
      const msg = guestError(e, "No se pudo rechazar.");
      if (msg) toast.error(msg); else dropGuest(guestId);
    }
  };
  const approveAllGuests = async () => {
    setGuestBusy(true);
    try {
      for (const g of [...guestRequests]) { await approveGuest(g.id); }
    } finally { setGuestBusy(false); }
  };

  // v5.5.3 · la lista de espera se lee del servidor al entrar y cada 5 s (no depende de haber estado conectado cuando llegó la solicitud)
  useEffect(() => {
    if (!isModerator || !classId) return undefined;
    let alive = true;
    const pull = async () => {
      try {
        const { data } = await api.get(`/live-classes/${classId}/guest-requests`);
        if (!alive || !Array.isArray(data)) return;
        const list = data.map((g) => ({ id: g.id, name: g.name || "Invitado" }));
        const fresh = list.filter((g) => !knownGuestIdsRef.current.has(g.id));
        fresh.forEach((g) => knownGuestIdsRef.current.add(g.id));
        if (fresh.length) playDing();
        setGuestRequests(list);
      } catch { /* sin permiso o sin conexión: se reintenta en 5 s */ }
    };
    pull();
    const t = setInterval(pull, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [isModerator, classId]);

  const stopScreenShare = async () => {
    const client = clientRef.current;
    if (screenTrackRef.current) {
      if (client) await client.unpublish(screenTrackRef.current).catch(() => {});
      screenTrackRef.current.close();
      screenTrackRef.current = null;
    }
    if (screenAudioTrackRef.current) {
      if (client) await client.unpublish(screenAudioTrackRef.current).catch(() => {});
      screenAudioTrackRef.current.close();
      screenAudioTrackRef.current = null;
    }
    if (localVideoTrackRef.current && client) {
      await client.publish(localVideoTrackRef.current).catch(() => {});
      if (localContainerRef.current) localVideoTrackRef.current.play(localContainerRef.current, { fit: "contain" });
    }
    setScreenSharing(false);
    setSharingUid(null);
    if (shareAnnounceIntervalRef.current) {
      clearInterval(shareAnnounceIntervalRef.current);
      shareAnnounceIntervalRef.current = null;
    }
    const ws = controlWsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "presence", data: { kind: "screen_share_stopped", agora_uid: uid.current } }));
    }
  };

  const stopScreenShareRef = useRef(null);
  stopScreenShareRef.current = stopScreenShare;

  const toggleScreen = async () => {
    if (!canShareScreen) {
      toast.error("El docente no ha habilitado compartir pantalla.");
      return;
    }
    const client = clientRef.current;
    const ws = controlWsRef.current;
    if (!client) {
      toast.error("La videollamada aún no está lista. Espera unos segundos e intenta de nuevo.");
      return;
    }
    if (!navigator.mediaDevices?.getDisplayMedia) {
      toast.error("Compartir pantalla no disponible en este dispositivo.");
      return;
    }

    if (screenSharing) {
      await stopScreenShare();
      return;
    } else {
      try {
        const created = await AgoraRTC.createScreenVideoTrack(
          { encoderConfig: SCREEN_PROFILE, optimizationMode: "motion" },
          shareWithAudio ? "enable" : "disable"
        );
        const screenVideoTrack = Array.isArray(created) ? created[0] : created;
        const screenAudioTrack = Array.isArray(created) ? created[1] : null;
        screenTrackRef.current = screenVideoTrack;
        screenAudioTrackRef.current = screenAudioTrack;
        if (localVideoTrackRef.current) await client.unpublish(localVideoTrackRef.current).catch(() => {});
        const toPublish = [screenVideoTrack, screenAudioTrack].filter(Boolean);
        await client.publish(toPublish);
        if (localContainerRef.current) screenVideoTrack.play(localContainerRef.current, { fit: "contain" });
        screenVideoTrack.on("track-ended", () => { stopScreenShare(); });
        setScreenSharing(true);
        setSharingUid(uid.current);
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "presence", data: { kind: "screen_share_started", agora_uid: uid.current } }));
        }
        if (shareAnnounceIntervalRef.current) clearInterval(shareAnnounceIntervalRef.current);
        shareAnnounceIntervalRef.current = setInterval(() => {
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "presence", data: { kind: "screen_share_started", agora_uid: uid.current } }));
          }
        }, 4000);
      } catch (e) {
        console.error("Screen share error:", e);
        // v5.6.3 · antes fallaba en silencio: ahora se explica el motivo al usuario.
        const n = e?.name || e?.code || "";
        if (screenTrackRef.current) { try { screenTrackRef.current.close(); } catch (_) { /* ya cerrada */ } screenTrackRef.current = null; }
        if (localVideoTrackRef.current && clientRef.current) {
          clientRef.current.publish(localVideoTrackRef.current).catch(() => {});
        }
        if (/NotAllowed|PERMISSION_DENIED|Permission/i.test(String(n) + String(e?.message || ""))) {
          toast.error("No se compartió la pantalla: el navegador no recibió permiso. Elige una ventana o pantalla en el aviso, o revisa el permiso de «Compartir pantalla» del navegador.");
        } else {
          toast.error("No se pudo compartir la pantalla en este dispositivo/navegador. Prueba con Chrome o Edge en computador.");
        }
      }
    }
  };

  const handleLeave = async () => {
    await leaveChannel();
    onLeave?.();
  };

  // Grid layout based on participant count
  const totalUsers = remoteUsers.length + 1;
  const gridClass = totalUsers <= 1 ? "grid-cols-1" :
    totalUsers <= 2 ? "grid-cols-2" :
    totalUsers <= 4 ? "grid-cols-2" :
    totalUsers <= 9 ? "grid-cols-3" :
    totalUsers <= 16 ? "grid-cols-4" :
    "grid-cols-5";

  const featuredUid = pinnedUid || sharingUid || null;

  // El protagonista soy yo (por fijarme o porque yo comparto pantalla).
  const isSelfFeatured = featuredUid === "local" || (featuredUid != null && String(featuredUid) === String(uid.current));

  // Re-bind local video track when featured layout changes (container remounts).
  // Si estoy compartiendo, en este contenedor va mi PANTALLA (no la cámara).
  useEffect(() => {
    if (!localContainerRef.current) return;
    if (screenSharing && screenTrackRef.current) {
      screenTrackRef.current.play(localContainerRef.current, { fit: "contain" });
    } else if (camOn && localVideoTrackRef.current) {
      localVideoTrackRef.current.play(localContainerRef.current, { fit: "contain" });
    }
  }, [featuredUid, camOn, screenSharing, floatMin.localThumb]);

  // Cámara propia flotante mientras comparto pantalla.
  useEffect(() => {
    if (screenSharing && camOn && localVideoTrackRef.current && floatCamRef.current) {
      localVideoTrackRef.current.play(floatCamRef.current, { fit: "cover" });
    }
  }, [screenSharing, camOn, featuredUid]);

  // NOTA: la optimización de calidad dual (alta para el protagonista, baja
  // para miniaturas) se retiró — causaba que la pantalla compartida se viera
  // borrosa también para quien debía recibir alta calidad. Si se retoma más
  // adelante, hay que confirmar primero que setRemoteVideoStreamType(uid, 0)
  // se aplique de forma confiable al uid que SÍ comparte antes de bajarle la
  // calidad a los demás.

  const handlePinToggle = (id) => setPinnedUid((prev) => prev === id ? null : id);

  const renderLocalVideo = (cls) => (
    <div
      onDoubleClick={() => setPinnedUid((prev) => prev === "local" ? null : "local")}
      className={`relative bg-[#1A1D2E] overflow-hidden border-2 ${
        isModerator ? "border-[#A43790]/50" : "border-white/10"
      } cursor-pointer ${cls}`}
      data-testid="local-video"
    >
      <div ref={localContainerRef} className="absolute inset-0" />
      {!camOn && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#1A1D2E]">
          <div className="w-16 h-16 rounded-full bg-[#A43790]/20 flex items-center justify-center">
            <VideoOff size={24} className="text-[#A43790]" />
          </div>
        </div>
      )}
      <div className="absolute bottom-1.5 left-1.5 bg-black/60 backdrop-blur-sm rounded-lg px-2 py-0.5 flex items-center gap-1.5">
        {micOn ? <Mic size={10} className="text-[#3AE880]" /> : <MicOff size={10} className="text-red-400" />}
        <span className="text-[10px] font-bold text-white/80">
          {authUser?.name?.split(" ")[0] || "Tú"} {isModerator && "(Docente)"}
        </span>
      </div>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <Loader2 className="animate-spin text-[#A43790]" size={28} />
        </div>
      )}
    </div>
  );

  if (!APP_ID) {
    return (
      <div className="flex items-center justify-center h-full bg-[#0d1025] text-white">
        <div className="text-center space-y-2">
          <AlertCircle size={32} className="mx-auto text-red-400" />
          <p className="font-bold">Agora no configurado</p>
          <p className="text-sm text-white/50">Falta REACT_APP_AGORA_APP_ID en el entorno</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden bg-[#0d1025] relative" data-testid="agora-room">
      {/* v5.7.3 · Banner de permisos de cámara y micrófono */}
      {!permBannerClosed && (permState !== "granted" || loading) && (
        <div
          role="status"
          data-testid="perm-banner"
          className={`shrink-0 flex items-start gap-2 px-3 py-2 text-[12px] font-semibold ${permState === "denied" || permState === "partial" ? "bg-[#A52258] text-white" : "bg-[#F5B82E] text-[#1F2547]"}`}
        >
          <AlertCircle size={15} className="mt-0.5 shrink-0" />
          <span className="flex-1">
            {permState === "denied"
              ? "No se concedieron los permisos de cámara y micrófono. Pulsa el candado junto a la dirección del navegador, permite Cámara y Micrófono y vuelve a entrar a la clase."
              : permState === "partial"
                ? "Solo se concedió el micrófono. Para que te vean, permite también la cámara (candado junto a la dirección)."
                : "Para participar en la videollamada necesitas otorgar los permisos de cámara y micrófono. Cuando el navegador lo pregunte, pulsa «Permitir»."}
          </span>
          {permState === "granted" || permState === "pending" ? null : (
            <button type="button" onClick={() => setPermBannerClosed(true)} aria-label="Cerrar aviso" className="shrink-0 opacity-80 hover:opacity-100"><X size={14} /></button>
          )}
        </div>
      )}
      {/* Video Area */}
      {!featuredUid ? (
        /* ── Modo grid uniforme (sin protagonista) ── */
        <div className={`flex-1 grid ${gridClass} gap-2 p-2 auto-rows-fr`}>
          {renderLocalVideo(`rounded-xl ${totalUsers <= 2 ? "col-span-1" : ""}`)}
          {remoteUsers.map((u) => (
            <RemoteUser key={u.uid} user={u} isLarge={false} identity={identities[u.uid]} onPin={handlePinToggle} />
          ))}
        </div>
      ) : (
        /* ── Modo protagonista + miniaturas ── */
        <div className="flex flex-col flex-1 gap-2 p-2 min-h-0">
          <div className="flex-1 min-h-0">
            {isSelfFeatured ? (
              renderLocalVideo("w-full h-full rounded-xl")
            ) : (
              (() => {
                const fu = remoteUsers.find((u) => String(u.uid) === String(featuredUid));
                if (fu) {
                  return (
                    <div className="w-full h-full">
                      <RemoteUser user={fu} isLarge={false} identity={identities[fu.uid]} onPin={handlePinToggle} className="w-full h-full" />
                    </div>
                  );
                }
                // No encontramos al usuario destacado en remoteUsers todavía — nunca
                // mostrar mi propia cámara como si fuera el contenido compartido.
                console.warn("[AgoraRoom] featuredUid no encontrado en remoteUsers:", featuredUid, "uids disponibles:", remoteUsers.map((u) => u.uid));
                return (
                  <div className="w-full h-full flex items-center justify-center text-white/40 text-sm">
                    Cargando pantalla compartida…
                  </div>
                );
              })()
            )}
          </div>
          <div className="flex gap-2 h-24 overflow-x-auto shrink-0">
            {remoteUsers.filter((u) => String(u.uid) !== String(featuredUid)).map((u) => (
              <RemoteUser key={u.uid} user={u} isLarge={false} identity={identities[u.uid]} onPin={handlePinToggle} className="w-32 h-full shrink-0" />
            ))}
          </div>
        </div>
      )}

      {/* v5.7.7 · Mi cámara flotante (arrastrable y minimizable) cuando otra persona es la protagonista */}
      {featuredUid && !isSelfFeatured && (
        <div
          onPointerDown={onDragDown("localThumb")} onPointerMove={onDragMove} onPointerUp={onDragEnd} onPointerCancel={onDragEnd}
          style={floatStyle("localThumb")}
          className={`absolute z-30 ${floatPos.localThumb ? "" : "bottom-16 right-2"} ${floatMin.localThumb ? "w-10 h-10 rounded-full" : "w-24 h-[72px] sm:w-32 sm:h-24 rounded-lg"} overflow-hidden shadow-xl cursor-grab active:cursor-grabbing`}
          data-testid="agora-local-float">
          <div className={floatMin.localThumb ? "hidden" : "w-full h-full"}>{renderLocalVideo("w-full h-full rounded-lg")}</div>
          <button type="button" data-nodrag onClick={() => setFloatMin((m) => ({ ...m, localThumb: !m.localThumb }))}
            aria-label={floatMin.localThumb ? "Mostrar mi cámara" : "Minimizar mi cámara"}
            className={`absolute z-40 text-white text-[10px] font-bold ${floatMin.localThumb ? "inset-0 bg-[#1A1D2E]" : "top-0.5 right-0.5 bg-black/50 rounded px-1"}`}>
            {floatMin.localThumb ? "▣" : "–"}
          </button>
        </div>
      )}

      {/* Cámara propia flotante mientras comparto pantalla */}
      {screenSharing && (
        <div
          onPointerDown={onDragDown("shareCam")} onPointerMove={onDragMove} onPointerUp={onDragEnd} onPointerCancel={onDragEnd}
          style={floatStyle("shareCam")}
          className={`absolute z-30 ${floatPos.shareCam ? "" : "top-3 right-3"} ${floatMin.shareCam ? "w-10 h-10 rounded-full" : "w-28 h-20 sm:w-36 sm:h-28 rounded-xl"} overflow-hidden border-2 border-white/30 shadow-xl bg-[#1A1D2E] cursor-grab active:cursor-grabbing`}
          data-testid="agora-float-cam">
          <div ref={floatCamRef} className={floatMin.shareCam ? "hidden" : "absolute inset-0"} />
          <button type="button" data-nodrag onClick={() => setFloatMin((m) => ({ ...m, shareCam: !m.shareCam }))}
            aria-label={floatMin.shareCam ? "Mostrar mi cámara" : "Minimizar mi cámara"}
            className={`absolute z-40 text-white text-[10px] font-bold ${floatMin.shareCam ? "inset-0" : "top-0.5 right-0.5 bg-black/50 rounded px-1"}`}>
            {floatMin.shareCam ? "▣" : "–"}
          </button>
          {!camOn && (
            <div className="absolute inset-0 flex items-center justify-center text-[10px] text-white/60 gap-1">
              <VideoOff size={14} /> Cámara apagada
            </div>
          )}
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="px-4 py-2 bg-red-500/20 border-t border-red-500/30 flex items-center gap-2">
          <AlertCircle size={14} className="text-red-400 shrink-0" />
          <p className="text-xs text-red-300">{error}</p>
        </div>
      )}

      {/* Chat panel */}
      {chatOpen && (
        <div className="absolute top-0 right-0 bottom-0 w-72 bg-[#12152B]/95 backdrop-blur-md border-l border-white/10 flex flex-col z-30" data-testid="agora-chat-panel">
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
            <span className="text-xs font-bold text-white/80 flex items-center gap-1.5"><MessageCircle size={13} /> Chat de clase</span>
            <button onClick={() => setChatOpen(false)} className="text-white/40 hover:text-white" data-testid="agora-chat-close"><X size={16} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2 min-h-0">
            {chatMessages.length === 0 && <p className="text-[11px] text-white/30 text-center pt-4">Sin mensajes aún</p>}
            {chatMessages.map((m, i) => (
              <div key={i} className={`flex flex-col ${m.mine ? "items-end" : "items-start"}`}>
                <span className="text-[10px] font-bold text-white/40 mb-0.5">{m.mine ? "Tú" : m.name}</span>
                <span className={`text-xs px-2.5 py-1.5 rounded-xl max-w-[200px] break-words ${m.mine ? "bg-[#A43790] text-white" : "bg-white/10 text-white/90"}`}>{m.text}</span>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <div className="px-2 py-2 border-t border-white/10 flex gap-1.5">
            <input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }}
              placeholder="Escribe…"
              maxLength={300}
              className="flex-1 bg-white/10 text-white text-xs rounded-lg px-3 py-2 outline-none placeholder-white/30"
              data-testid="agora-chat-input"
            />
            <button onClick={sendChatMessage} className="w-8 h-8 rounded-lg bg-[#A43790] hover:bg-[#C44DB0] flex items-center justify-center text-white" data-testid="agora-chat-send"><Send size={14} /></button>
          </div>
        </div>
      )}

      {/* v5.5.3 · Sala de espera: aviso fijo con Admitir / Rechazar (no desaparece solo) */}
      {isModerator && guestRequests.length > 0 && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-40 w-[min(92%,30rem)] rounded-2xl bg-[#12152B]/95 backdrop-blur-md border-2 border-[#FFCC28] shadow-2xl p-3" data-testid="agora-waiting-room" role="alert">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-sm font-extrabold text-[#FFCC28]">
              {guestRequests.length === 1 ? "1 persona quiere entrar" : `${guestRequests.length} personas quieren entrar`}
            </p>
            {guestRequests.length > 1 && (
              <button onClick={approveAllGuests} disabled={guestBusy} data-testid="agora-admit-all"
                className="px-3 py-1.5 rounded-lg bg-[#3AE880] text-[#0d1025] text-xs font-extrabold hover:brightness-110 disabled:opacity-60">
                Admitir a todos
              </button>
            )}
          </div>
          <ul className="space-y-2 max-h-56 overflow-y-auto">
            {guestRequests.map((g) => (
              <li key={g.id} className="flex items-center justify-between gap-2 bg-white/5 rounded-xl px-3 py-2" data-testid={`waiting-${g.id}`}>
                <span className="text-sm font-bold text-white truncate">{g.name}</span>
                <span className="flex gap-2 shrink-0">
                  <button onClick={() => approveGuest(g.id)} data-testid={`waiting-admit-${g.id}`}
                    className="px-4 py-2 rounded-lg bg-[#3AE880] text-[#0d1025] text-sm font-extrabold hover:brightness-110">Admitir</button>
                  <button onClick={() => rejectGuest(g.id)} data-testid={`waiting-reject-${g.id}`}
                    className="px-3 py-2 rounded-lg bg-red-500/80 text-white text-sm font-extrabold hover:bg-red-500">Rechazar</button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Guest requests panel */}
      {guestPanelOpen && isModerator && (
        <div className="absolute top-0 right-0 bottom-0 w-72 bg-[#12152B]/95 backdrop-blur-md border-l border-white/10 flex flex-col z-30" data-testid="agora-guest-panel">
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
            <span className="text-xs font-bold text-white/80 flex items-center gap-1.5"><Users size={13} /> Solicitudes de invitados</span>
            <button onClick={() => setGuestPanelOpen(false)} className="text-white/40 hover:text-white"><X size={16} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2 min-h-0">
            {guestRequests.length === 0 && <p className="text-[11px] text-white/30 text-center pt-4">Sin solicitudes pendientes</p>}
            {guestRequests.map((g) => (
              <div key={g.id} className="bg-white/5 rounded-lg p-2.5 flex items-center justify-between gap-2">
                <span className="text-xs text-white/80 font-medium truncate">{g.name}</span>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => approveGuest(g.id)} data-testid={`approve-${g.id}`}
                    className="px-2.5 py-1 rounded-md bg-[#3AE880]/20 text-[#3AE880] text-[10px] font-bold hover:bg-[#3AE880]/30">Aprobar</button>
                  <button onClick={() => rejectGuest(g.id)} data-testid={`reject-${g.id}`}
                    className="px-2.5 py-1 rounded-md bg-red-500/20 text-red-400 text-[10px] font-bold hover:bg-red-500/30">Rechazar</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Controls bar */}
      <div className="shrink-0 bg-[#12152B] border-t border-white/10 px-4 py-3 flex items-center justify-center gap-3" data-testid="agora-controls">
        <button
          onClick={toggleMic}
          data-testid="agora-mic-toggle"
          className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${
            micOn ? "bg-white/10 hover:bg-white/20 text-white" : "bg-red-500/80 text-white"
          }`}
          title={micOn ? "Silenciar micrófono" : "Activar micrófono"}
        >
          {micOn ? <Mic size={18} /> : <MicOff size={18} />}
        </button>

        <button
          onClick={toggleCam}
          data-testid="agora-cam-toggle"
          className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${
            camOn ? "bg-white/10 hover:bg-white/20 text-white" : "bg-red-500/80 text-white"
          }`}
          title={camOn ? "Apagar cámara" : "Encender cámara"}
        >
          {camOn ? <Video size={18} /> : <VideoOff size={18} />}
        </button>

          {canShareScreen && (
          <button
            onClick={toggleScreen}
            data-testid="agora-screen-share"
            className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${
              screenSharing ? "bg-[#A43790] text-white" : "bg-white/10 hover:bg-white/20 text-white"
            }`}
            title={screenSharing ? "Dejar de compartir" : "Compartir pantalla"}
          >
            <MonitorUp size={18} />
          </button>
          )}
          {isModerator && (
            <button
              onClick={toggleStudentsCanShare}
              data-testid="agora-students-share-toggle"
              className={`h-11 px-3 rounded-full flex items-center gap-1.5 text-[11px] font-bold transition-all ${
                studentsCanShare ? "bg-[#3AE880]/20 text-[#3AE880]" : "bg-white/10 hover:bg-white/20 text-white/80"
              }`}
              title="Permitir o no que estudiantes e invitados compartan su pantalla"
            >
              <MonitorUp size={14} /> Alumnos e invitados: {studentsCanShare ? "Sí" : "No"}
            </button>
          )}
          {canShareScreen && (
          <label className="flex items-center gap-1 text-[10px] text-white/70 cursor-pointer select-none" title="Úsalo solo para compartir videos; puede causar eco">
            <input type="checkbox" checked={shareWithAudio} onChange={(e) => setShareWithAudio(e.target.checked)}
              className="w-3 h-3" />
            Incluir audio (puede causar eco)
          </label>
          )}

        <div className="w-px h-8 bg-white/10 mx-1" />

        {isModerator && (
          <button onClick={handleGuestLink} disabled={guestLinkLoading}
            data-testid="agora-guest-link"
            className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${
              guestLink ? "bg-[#3AE880]/20 text-[#3AE880]" : "bg-white/10 hover:bg-white/20 text-white"
            }`}
            title={guestLink ? "Copiar link de invitado" : "Generar link de invitado"}>
            {guestLinkLoading ? <Loader2 size={18} className="animate-spin" /> : <Link2 size={18} />}
          </button>
        )}

        {isModerator && (
          <button onClick={() => setGuestPanelOpen((v) => !v)}
            data-testid="agora-guest-requests-toggle"
            className={`relative w-11 h-11 rounded-full flex items-center justify-center transition-all ${
              guestPanelOpen ? "bg-[#A43790] text-white" : "bg-white/10 hover:bg-white/20 text-white"
            }`}
            title="Solicitudes de invitados">
            <Users size={18} />
            {guestRequests.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{guestRequests.length}</span>
            )}
          </button>
        )}

        <button
          onClick={() => setChatOpen((v) => !v)}
          data-testid="agora-chat-toggle"
          className={`relative w-11 h-11 rounded-full flex items-center justify-center transition-all ${
            chatOpen ? "bg-[#A43790] text-white" : "bg-white/10 hover:bg-white/20 text-white"
          }`}
          title={chatOpen ? "Cerrar chat" : "Abrir chat"}
        >
          <MessageCircle size={18} />
          {unreadChat > 0 && !chatOpen && (
            <span data-testid="agora-chat-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center ring-2 ring-[#0d1025] animate-pulse">
              {unreadChat > 9 ? "9+" : unreadChat}
            </span>
          )}
        </button>

        <button
          onClick={handleLeave}
          data-testid="agora-leave"
          className="w-11 h-11 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-all"
          title="Salir de la clase"
        >
          <PhoneOff size={18} />
        </button>

        {/* Participant count & Recording indicator */}
        {isRecording && (
          <div className="ml-3 flex items-center gap-1.5 bg-red-500/20 rounded-full px-3 py-1.5" data-testid="agora-recording-badge">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[10px] font-bold text-red-400">REC</span>
          </div>
        )}
        <div className={`${isRecording ? "ml-1" : "ml-3"} flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5`}>
          <Users size={13} className="text-white/50" />
          <span className="text-xs font-bold text-white/70">{totalUsers}</span>
        </div>
      </div>
    </div>
  );
}
